import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CreditCard, FileText, Inbox, LayoutDashboard, Loader2, LogOut, MessageSquare, Package, Star, BookOpen } from 'lucide-react';
import { useSEO } from '../seo';
import { Reveal } from '../components/Reveal';
import { PartnerBookings, PartnerMessages, PartnerRequests } from '../components/partner/PartnerRequests';
import { PartnerProducts } from '../components/partner/PartnerProducts';
import { PartnerAvailability, PartnerPayouts } from '../components/partner/PartnerAvailability';
import { apiFetch, formatEur, selectCls, statusBadge } from '../utils/operations';
import { logout, useRequireAuth } from '../utils/auth';

const MENU = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Overview' },
  { id: 'requests', icon: Inbox, label: 'Requests' },
  { id: 'bookings', icon: Calendar, label: 'Bookings' },
  { id: 'products', icon: Package, label: 'Products' },
  { id: 'trade-catalog', icon: BookOpen, label: 'Trade Catalogue' },
  { id: 'availability', icon: Calendar, label: 'Availability' },
  { id: 'media', icon: FileText, label: 'Media' },
  { id: 'reviews', icon: Star, label: 'Reviews' },
  { id: 'payments', icon: CreditCard, label: 'Payouts' },
  { id: 'messages', icon: MessageSquare, label: 'Messages' },
];

export default function PartnerDashboard() {
  useSEO('Área de Parceiro | Eco Collection', 'Dashboard operacional do parceiro local Eco Collection.');
  const { user, ready } = useRequireAuth();
  const navigate = useNavigate();
  const isPartnerRole = user?.role === 'partner';
  const [tab, setTab] = useState('dashboard');
  const [partners, setPartners] = useState([]);
  const [partnerName, setPartnerName] = useState(() => localStorage.getItem('eco-partner-name') || '');
  const [data, setData] = useState({ requests: [], vouchers: [], products: [], payouts: [], reviews: [], tradeCatalog: null });
  const partner = partners.find((p) => p.name === partnerName) || partners[0];

  const load = useCallback(async () => {
    if (!user) return;
    let allPartners = [];
    if (isPartnerRole) {
      const mine = await apiFetch('/partners/me').catch(() => null);
      allPartners = mine ? [mine] : [];
    } else {
      allPartners = await apiFetch('/partners/all').catch(() => []);
    }
    setPartners(allPartners);
    const selected = isPartnerRole ? user.partner_name : (partnerName || allPartners[0]?.name || '');
    if (selected && selected !== partnerName) {
      setPartnerName(selected);
      localStorage.setItem('eco-partner-name', selected);
    }
    if (!selected) return;
    const [requests, vouchers, products, payouts, reviews, tradeCatalog] = await Promise.all([
      apiFetch('/requests').catch(() => []),
      apiFetch('/vouchers').catch(() => []),
      apiFetch(`/partner/products?partner_name=${encodeURIComponent(selected)}`).catch(() => []),
      apiFetch(`/payouts?partner_name=${encodeURIComponent(selected)}`).catch(() => []),
      apiFetch('/admin/reviews').catch(() => []),
      apiFetch('/v1/partner/catalog').catch(() => null),
    ]);
    setData({ requests, vouchers, products, payouts, reviews: reviews.filter((r) => r.partner_name === selected), tradeCatalog });
  }, [partnerName, user, isPartnerRole]);

  useEffect(() => { if (ready) load(); }, [load, ready]);
  const assigned = useMemo(() => partner ? data.requests.filter((r) => r.partner_name === partner.name) : [], [data.requests, partner]);
  const pendingRequests = assigned.filter((r) => ['request_sent', 'waiting_partner', 'pending'].includes(r.status));
  const bookings = assigned.filter((r) => ['payment_pending', 'confirmed', 'voucher_issued', 'completed'].includes(r.status));

  const choosePartner = (name) => {
    setPartnerName(name);
    localStorage.setItem('eco-partner-name', name);
  };

  if (!ready) {
    return (
      <div data-testid="partner-loading" className="grid min-h-screen place-items-center bg-eco-cream">
        <Loader2 size={22} className="animate-spin text-eco-gold" />
      </div>
    );
  }

  return (
    <div data-testid="partner-dashboard" className="min-h-screen bg-eco-cream">
      <section className="border-b border-eco-ink/10 bg-white pt-28 pb-8">
        <div className="eco-container flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eco-eyebrow">Rede Local</p>
            <h1 className="eco-page-title mt-2 text-eco-ink">Área de Parceiro</h1>
            <p className="eco-secondary mt-2 max-w-2xl text-eco-ink/60">Pedidos, produtos, disponibilidade, vouchers, reviews e payouts — apenas dados reais.</p>
          </div>
          <button type="button" data-testid="partner-logout" onClick={() => logout(navigate)} className="eco-btn-text text-eco-ink/60 hover:text-red-500"><LogOut size={15} /> Sair</button>
        </div>
      </section>
      <section className="py-8 md:py-10">
        <div className="eco-container grid gap-6 md:grid-cols-[208px_minmax(0,1fr)]">
          <Reveal>
            <div className="space-y-4">
              {!isPartnerRole && (
                <select data-testid="partner-select" value={partner?.name || ''} onChange={(e) => choosePartner(e.target.value)} className={`${selectCls} w-full bg-white px-4 py-3 text-sm`}>
                  {partners.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              )}
              {isPartnerRole && <p data-testid="partner-current" className="rounded-lg bg-eco-green/5 px-4 py-3 text-sm font-semibold text-eco-green">{partner?.name || user?.partner_name}</p>}
              <nav className="flex flex-wrap gap-2 md:flex-col" data-testid="partner-menu">
                {MENU.map((m) => <button key={m.id} data-testid={`partner-menu-${m.id}`} onClick={() => setTab(m.id)} className={`flex min-h-10 items-center gap-2.5 rounded-lg px-3.5 py-2 text-left text-sm font-medium transition-colors ${tab === m.id ? 'bg-eco-green text-white' : 'bg-white text-eco-ink/65 hover:bg-eco-green/10'}`}><m.icon size={15} /> {m.label}</button>)}
              </nav>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {!partner ? (
              <p className="rounded-2xl border border-dashed border-eco-ink/15 bg-white py-16 text-center text-sm text-eco-ink/50">Sem parceiros operacionais.</p>
            ) : (
              <>
                {tab === 'dashboard' && (
                  <div data-testid="partner-overview" className="space-y-6">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      {[['Pedidos pendentes', pendingRequests.length], ['Bookings', bookings.length], ['Produtos', data.products.length], ['Payout pendente', formatEur(data.payouts.filter((p) => p.status !== 'paid').reduce((n, p) => n + Number(p.partner_amount_eur || 0), 0))]].map(([label, value]) => (
                        <div key={label} className="eco-admin-card p-5"><p className="text-[13px] font-medium text-eco-ink/55">{label}</p><p className="eco-number mt-2 text-[28px] leading-tight font-semibold tracking-[-0.02em] text-eco-green">{value}</p></div>
                      ))}
                    </div>
                    <div className="rounded-2xl border border-eco-ink/10 bg-white p-6"><p className="text-sm font-semibold text-eco-ink">{partner.name}</p><p className="mt-1 text-sm text-eco-ink/55">{partner.island} · {TABS_SAFE_TYPE(partner.type)} · {partner.published ? 'Publicado' : 'Rascunho'}</p></div>
                  </div>
                )}
                {tab === 'requests' && <PartnerRequests partner={partner} requests={data.requests} onRefresh={load} />}
                {tab === 'bookings' && <PartnerBookings partner={partner} requests={data.requests} vouchers={data.vouchers} />}
                {tab === 'products' && <PartnerProducts partner={partner} products={data.products} defaultType="all" onRefresh={load} />}
                {tab === 'trade-catalog' && <div data-testid="partner-trade-catalog" className="space-y-5"><div className="rounded-2xl border border-eco-ink/10 bg-white p-6"><h2 className="text-xl font-semibold text-eco-ink">Authorised canonical catalogue</h2><p className="mt-2 text-sm text-eco-ink/55">Read-only catalogue shared with Experiences, Full Packages, Shore Excursions, Stays and Transfers. Commercial rates and private terms are not included.</p></div>{data.tradeCatalog ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Object.entries(data.tradeCatalog).map(([type, items]) => <div key={type} className="rounded-xl border border-eco-ink/10 bg-white p-5"><p className="text-xs font-bold uppercase tracking-wide text-eco-ink/45">{type.replaceAll('_',' ')}</p><p className="mt-2 text-3xl font-semibold text-eco-green">{items.length}</p><p className="mt-1 text-xs text-eco-ink/50">published products</p></div>)}</div> : <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-10 text-center text-sm text-eco-ink/50">Catalogue unavailable in this environment.</p>}<div className="rounded-xl bg-eco-cream p-5"><h3 className="font-semibold text-eco-ink">Partner API</h3><p className="mt-2 text-sm text-eco-ink/55">Versioned endpoints use the current authenticated partner session. External API-key issuance is not implemented.</p></div></div>}
                {tab === 'availability' && (
                  <div className="space-y-6">
                    <PartnerAvailability products={data.products} onRefresh={load} />
                    <div className="space-y-3" data-testid="partner-calendar-summary">
                      {data.products.flatMap((p) => (p.availability || []).map((s) => ({ ...s, product: p.name }))).sort((a, b) => a.date.localeCompare(b.date)).map((s) => (
                        <div key={`${s.product}-${s.date}`} data-testid={`calendar-slot-${s.date}-${s.product}`} className="flex items-center justify-between rounded-xl border border-eco-ink/10 bg-white px-4 py-3 text-sm"><span>{s.date} · {s.product}</span><span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(s.status)}`}>{s.status}</span></div>
                      ))}
                      {data.products.every((p) => !p.availability?.length) && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-10 text-center text-sm text-eco-ink/50">Sem datas configuradas.</p>}
                    </div>
                  </div>
                )}
                {tab === 'media' && (
                  <div className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-14 text-center" data-testid="partner-media-panel">
                    <FileText className="mx-auto text-eco-gold" size={24} />
                    <p className="mt-3 text-lg font-semibold text-eco-ink">Media</p>
                    <p className="mx-auto mt-2 max-w-md text-sm leading-[1.55] text-eco-ink/50">Carregue imagens ao criar produtos; os ficheiros são otimizados e ligados ao produto real.</p>
                  </div>
                )}
                {tab === 'messages' && <PartnerMessages />}
                {tab === 'reviews' && (
                  <div className="space-y-3" data-testid="partner-review-list">
                    {data.reviews.map((r) => <div key={r.id} data-testid={`partner-review-${r.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5"><p className="text-sm font-semibold text-eco-ink">{r.experience || r.package_name} · {r.rating}/5</p><p className="eco-secondary mt-1 text-eco-ink/60">“{r.text}”</p><span className={`mt-3 inline-block rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(r.status)}`}>{r.status}</span></div>)}
                    {data.reviews.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-10 text-center text-sm text-eco-ink/50">Sem reviews reais ainda.</p>}
                  </div>
                )}
                {tab === 'payments' && <PartnerPayouts payouts={data.payouts} />}
              </>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const TABS_SAFE_TYPE = (type) => ({ agency: 'Agência', operator: 'Operador', dmc: 'DMC', guide: 'Guia', accommodation: 'Alojamento', experience: 'Experiências', transport: 'Transporte' }[type] || type);
